import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useQueueSettings from '@/hooks/dq/useSettings';
import useAlerts from '@/hooks/useAlerts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

const Dialog = styled(StyledDialog)`
  width: 460px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e7e7e7;
  box-shadow: 0px 1px 0px 0px #22222214;
`;

const Body = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Footer = styled.div`
  border-radius: 0 0 4px 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  height: 112px;
  padding: 0 24px;
  box-shadow: 0px -1px 8px 0px #2222220f;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const Switch = styled.div`
  background: ${({ theme }) => theme.colors.white};
`;

const Description = styled(Text)`
  color: ${({ theme }) => theme.colors.gray};
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Backend defaults (see feat/dq-repost-automation on bps-backend).
export const REPOST_AUTOMATION_DEFAULTS = {
  enabled: false,
  top_percent: 30,
  delay_days: 30,
  window_months: 6,
  exclude_news_from_reference: true,
  min_sample_size: 30,
};

type FormDataT = {
  repost_automation_settings: {
    enabled: boolean;
    top_percent: string;
    delay_days: string;
    window_months: string;
    exclude_news_from_reference: boolean;
    min_sample_size: string;
  };
};

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const RepostAutomationModal: FC<Props> = ({ onClose, activePage }) => {
  const queryClient = useQueryClient();
  const settings = useQueueSettings(activePage);
  const [network] = useNetwork();
  const alerts = useAlerts();

  const current = settings?.repostAutomationSettings;

  const {
    control,
    reset,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      repost_automation_settings: {
        enabled: current?.enabled ?? REPOST_AUTOMATION_DEFAULTS.enabled,
        top_percent: `${
          current?.top_percent ?? REPOST_AUTOMATION_DEFAULTS.top_percent
        }`,
        delay_days: `${
          current?.delay_days ?? REPOST_AUTOMATION_DEFAULTS.delay_days
        }`,
        window_months: `${
          current?.window_months ?? REPOST_AUTOMATION_DEFAULTS.window_months
        }`,
        exclude_news_from_reference:
          current?.exclude_news_from_reference ??
          REPOST_AUTOMATION_DEFAULTS.exclude_news_from_reference,
        min_sample_size: `${
          current?.min_sample_size ?? REPOST_AUTOMATION_DEFAULTS.min_sample_size
        }`,
      },
    },
  });

  const onSubmit = () => {
    clearErrors();
    handleSubmit(async (data) => {
      const s = data.repost_automation_settings;

      // Send only known keys, integers as numbers. Backend validates ranges
      // and returns 422 keyed as repost_automation_settings.<field>.
      const payload = {
        repost_automation_settings: {
          enabled: s.enabled,
          top_percent: parseInt(s.top_percent, 10),
          delay_days: parseInt(s.delay_days, 10),
          window_months: parseInt(s.window_months, 10),
          exclude_news_from_reference: s.exclude_news_from_reference,
          min_sample_size: parseInt(s.min_sample_size, 10),
        },
      };

      try {
        await api.post('/v1/editorial/queue/settings', {
          ...payload,
          network,
          page_id: activePage.id,
        });

        await queryClient.invalidateQueries([
          'editorial_distribution_queue_channels',
        ]);

        alerts.success('Settings saved');

        reset(data);
      } catch (err) {
        handleFormErrors(err, setError, alerts, true);
      }
    })();
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">Repost Automation</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Body>
          <Switch>
            <FormField<FormDataT>
              control={control}
              name="repost_automation_settings.enabled"
              type="switch"
              headline="Automatically repost top-performing posts"
              description="When a post's 24h site-visits land in the top N% of this page's trailing distribution, a repost is scheduled after the delay below (skipped if the same link was posted or queued in the meantime)."
            />
          </Switch>
          <Fields>
            <FormField<FormDataT>
              control={control}
              type="integer"
              label="Top percent"
              subLabel="1–99. A post qualifies when its 24h site-visits are in the top N% of the reference distribution."
              name="repost_automation_settings.top_percent"
            />
            <FormField<FormDataT>
              control={control}
              type="integer"
              label="Delay (days)"
              subLabel="1–365. Days after the 24h evaluation before the repost is inserted."
              name="repost_automation_settings.delay_days"
            />
            <FormField<FormDataT>
              control={control}
              type="integer"
              label="Window (months)"
              subLabel="1–24. Trailing window used to build the reference distribution."
              name="repost_automation_settings.window_months"
            />
            <FormField<FormDataT>
              control={control}
              type="integer"
              label="Minimum sample size"
              subLabel="Minimum number of posts in the window before the automation acts."
              name="repost_automation_settings.min_sample_size"
            />
            <Switch>
              <FormField<FormDataT>
                control={control}
                name="repost_automation_settings.exclude_news_from_reference"
                type="switch"
                headline="Exclude News posts from reference distribution"
                description="News-department posts are never reposted regardless of this flag."
              />
            </Switch>
          </Fields>
          <Description $type="caption">
            Available for the Facebook network only.
          </Description>
        </Body>
        <Footer>
          <Button
            type="button"
            color="primary"
            disabled={isSubmitting || !isDirty}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Save
          </Button>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default RepostAutomationModal;
